import { useEffect, useState } from 'react';
import './App.scss';
import { About } from './components/About';
import { CardGrid, MARGIN_SIZE } from './components/CardGrid';
import { GameStats } from './components/GameStats';
import { HandResult } from './components/HandResult';
import { HowToPlay } from './components/HowToPlay';
import { Card } from './game/card';
import { PokerHandResult } from './game/poker-hand-checker';
import { WaterfallPokerGame } from './game/waterfall-poker-game';
import { StateService } from './storage-service';
import { VERSION } from './version';
import { Splash } from './components/Splash';
import { WebAudioService } from './audio-service';
import { GameSettings } from './components/GameSettings';

export type GameState = {
    highScore: number;
    discards: number;
    quick: boolean;
    version: string;
};

const stateSvc = new StateService();
const audioSvc = new WebAudioService();

let game: WaterfallPokerGame;

function App() {
    const [loading, setLoading] = useState(true);
    const [stacks, setStacks] = useState<Card[][]>([]);
    const [handResult, setHandResult] = useState<PokerHandResult>();
    const [ameSettingsVisible, setGameSettingsVisible] = useState(false);
    const [gameOver, setGameOver] = useState<boolean>(false);
    const [invalidHand, setInvalidHand] = useState<boolean>(false);
    const [showHelp, setShowHelp] = useState<boolean>(false);
    const [showAbout, setShowAbout] = useState<boolean>(false);
    const [playRowHeight, setPlayRowHeight] = useState(0);
    const [gameState, setGameState] = useState<GameState>({ version: "", highScore: 0, discards: 5, quick: false });

    useEffect(() => {
        if (!gameState.version) {
            stateSvc.loadState()
            .then(state => {
                setGameState(state);
            })
            .catch(() => {
                setShowHelp(true);
            })
            .finally(() => {
                gameState.version = VERSION;
            });
            
            // window.addEventListener("resize", () => {
            //     console.log("resized");
            //     setStacks(stacks.slice());
            // })
        }
    }, [gameState]);//[setStacks, stacks]);

    const playRowStyle = { top: 4 * playRowHeight + 12, height: playRowHeight };

    return (
        <div className="App">
            {loading ? <Splash audioSvc={audioSvc} onLoaded={() => assetsLoaded()}/> :
            game && <>
            <div className='play-row' style={playRowStyle}></div>
            <div className='controls column'>
                <GameStats score={game?.points} discards={game?.discardsLeft} cards={game?.cardsLeft} highScore={gameState.highScore}/>
                <div className='menu'>
                    <button onClick={() => endGame()}>End Game</button>
                    <button onClick={() => setShowHelp(true)}>How to Play</button>
                    <button onClick={() => setShowAbout(true)}>About</button>
                </div>
                <div className='play' style={playRowStyle}>
                    <div className='discards'>{game?.discardsLeft} Discards remaining</div>
                    <div className='arrow-right'></div>
                    <button onClick={() => playHand()}>Play Hand</button>
                </div>
            </div>
            {game &&
                <CardGrid stacks={stacks} deck={game.deck} cardClicked={c => cardClicked(c)} playHand={() => playHand()} cardHeightChanged={h => cardHeightChanged(h)}/>
            }
            <div className='placeholder column'>&nbsp;</div>
            </>}

            {handResult && <HandResult result={handResult}/>}
            
            {invalidHand &&
            <div className='message'>
                <header>Not a valid hand</header>
            </div>}

            {ameSettingsVisible &&
                <GameSettings discards={gameState.discards} quick={gameState.quick} onPlay={(d, q) => newGame(d, q)}/>}
                
            {gameOver &&
            <div className='message'>
                <header>Game Over</header>
                <div>- Score {game.points} -</div>
                {game.points === gameState.highScore && <div>You got a new high score!</div>}
                <button onClick={() => showGameSettings()}>Play Again</button>
            </div>}

            {showAbout && <About onClose={() => setShowAbout(false)}/>}

            {showHelp && <HowToPlay onClose={() => setShowHelp(false)}/>}
        </div>
    );

    function assetsLoaded(): void {
        // newGame();
        setLoading(false);
        setGameSettingsVisible(true);
    }

    function newGame(discards?: number, quick?: boolean): void {
        setGameSettingsVisible(false);

        if (discards != null) gameState.discards = discards;
        if (quick != null) gameState.quick = quick;
        stateSvc.saveState(gameState);

        // clear board
        setStacks([[], [], [], [], []]);
        setTimeout(() => {
            setGameOver(false);
            game = new WaterfallPokerGame(gameState.quick, gameState.discards);
            setStacks(game.stacks);
            audioSvc.playGameStart();
        });
    }
    
    function cardClicked(col: number): void {
        if (gameOver) return;

        if (game.canDiscard()) {
            if (game.discard(col)) {
                audioSvc.playCardFlip()
                // console.log(game.stacks);
                setStacks(game.stacks.slice());

                if (!game.canDiscard()) {
                    playHand();
                }
            }
        }
    }

    function playHand(): void {
        if (gameOver) return;
        
        const result = game.playHand();
        if (result) {
            setStacks(game.stacks.slice());
            setHandResult(result);
            setTimeout(() => audioSvc.playValidHand(), 500);

            setTimeout(() => {
                setHandResult(undefined);
                // Check if all stacks have 0 or 1 card left
                if (game.stacks.every(s => s.length <= 1)) {
                    if (!game.checkHand()) {
                        endGame();
                    }
                }
            }, 3000);

            // console.log(stacks);
            if (game.points > gameState.highScore) {
                gameState.highScore = game.points;
                stateSvc.saveState(gameState);
            }
        }
        else if (game.discardsLeft === 0) {
            endGame();
        }
        else {
            setInvalidHand(true);
            setTimeout(() => setInvalidHand(false), 2000);
        }
    }

    function endGame(): void {
        if (game.points > 0) {
            setGameOver(true);
        }
        else {
            setGameSettingsVisible(true);
        }
    }
    
    function cardHeightChanged(h: number): void {
        setPlayRowHeight(h + MARGIN_SIZE);
    }

    function showGameSettings(): void {
        setGameOver(false);
        setGameSettingsVisible(true);
    }
}

export default App;
